import { EPSILON } from "../constants";
import type { ResampleFilter } from "./ResampleFilter";
import type { WeightingFunction } from "../weighting-function";

/**
 * Base ResampleFilter interface implementation.
 *
 * @see https://www.imagemagick.org/Usage/filter/#filter Resampling Filters at ImageMagick docs
 * @see https://www2.eecs.berkeley.edu/Pubs/TechRpts/1989/CSD-89-516.pdf Fundamentals of Texture Mapping and
 * Image Warping by Paul S. Heckbert, page 41, section 3.4, 3.5
 * @see https://imagemagick.org/api/MagickCore/resize_8c_source.html#l00757 AquireResizeFilter at ImageMagick source
 */
export class BaseResampleFilter implements ResampleFilter {
  /**
   * Window support, usually equal to support (expert only).
   */
  private readonly windowSupport: number;

  /**
   * BaseResampleFilter constructor.
   *
   * @param filterFunction Filtering function.
   * @param windowingFunction Windowing function.
   * @param support Filter region of support - the filter support limit.
   * @param scale Dimension scaling to fit window support (usually 1.0).
   * @param [blur=1] X-scale (blur-sharpen).
   * @param [windowSupport=null] Window support, usually equal to support (expert only).
   */
  constructor(
    private readonly filterFunction: WeightingFunction,
    private readonly windowingFunction: WeightingFunction,
    private readonly support: number,
    private readonly scale: number,
    private readonly blur = 1,
    windowSupport?: number
  ) {
    this.windowSupport =
      windowSupport !== undefined ? windowSupport : this.support;
  }

  /**
   * @inheritDoc
   */
  getWorkingSupport(): number {
    return this.support * this.blur;
  }

  /**
   * @inheritDoc
   */
  getWeight(x: number): number {
    const xBlur = x / this.blur;
    const scale = xBlur < EPSILON ? 1 : this.window(this.scale * xBlur);
    return scale * this.filter(xBlur);
  }

  /**
   * @param x
   */
  private filter(x: number): number {
    return this.filterFunction(x, this.support, this.windowSupport);
  }

  /**
   *
   * @param x
   */
  private window(x: number): number {
    return this.windowingFunction(x, this.support, this.windowSupport);
  }
}
